import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Switch } from 'antd';
import { breedsService, Breed } from '../services/breedsService';
import { petCategoriesService, PetCategory } from '../services/petCategoriesService';

const { Title } = Typography;
const { Option } = Select;

const Breeds: React.FC = () => {
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [categories, setCategories] = useState<PetCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBreed, setEditingBreed] = useState<Breed | null>(null);
  const [form] = Form.useForm();
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);

  useEffect(() => {
    fetchBreeds();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchBreeds();
  }, [categoryFilter]);

  const fetchBreeds = async () => {
    try {
      setLoading(true);
      const response = await breedsService.getBreeds(categoryFilter, true); // include inactive breeds
      setBreeds(response.data?.breeds || response.data || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch breeds');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await petCategoriesService.getCategories();
      setCategories(response.data?.categories || response.data || []);
    } catch (error: any) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleAdd = () => {
    setEditingBreed(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (breed: Breed) => {
    setEditingBreed(breed);
    form.setFieldsValue(breed);
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingBreed) {
        await breedsService.updateBreed(editingBreed._id, values);
        message.success('Breed updated successfully');
      } else {
        await breedsService.createBreed(values);
        message.success('Breed created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      fetchBreeds();
    } catch (error: any) {
      message.error(error.message || 'Failed to save breed');
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await breedsService.toggleBreedStatus(id);
      message.success(`Breed ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchBreeds();
    } catch (error: any) {
      message.error(error.message || 'Failed to toggle breed status');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await breedsService.deleteBreed(id);
      message.success('Breed deleted successfully');
      fetchBreeds();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete breed');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
    },
    {
      title: 'Category',
      dataIndex: 'petCategory',
      key: 'petCategory',
      render: (categoryId: string) => {
        const category = categories.find(c => c._id === categoryId);
        return category?.name || 'Unknown';
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean, record: Breed) => (
        <Space>
          <Switch
            checked={isActive}
            onChange={() => handleToggleStatus(record._id, isActive)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
          <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: Breed) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this breed?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>Breeds</Title>
          </Col>
          <Col>
            <Space>
              <Select
                placeholder="Filter by Category"
                style={{ width: 200 }}
                value={categoryFilter}
                onChange={(value) => setCategoryFilter(value)}
                allowClear
              >
                {categories.map((cat) => (
                  <Option key={cat._id} value={cat._id}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
              <Button icon={<ReloadOutlined />} onClick={fetchBreeds}>
                Refresh
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                Add Breed
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={breeds}
          rowKey="_id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingBreed ? 'Edit Breed' : 'Add Breed'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="petCategory" label="Category" rules={[{ required: true }]}>
            <Select placeholder="Select category">
              {categories.map(cat => (
                <Option key={cat._id} value={cat._id}>{cat.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
            <Input type="checkbox" /> Active
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Breeds;

